// Generic localStorage persistence for any "bulk task + session_id" progress
// widget (bulk import, bulk delete, ...). Each feature picks its own
// storageKey so sessions never collide; the shape itself is shared.

export interface TaskProgressSession {
    sessionId: string;
    totalTasks: number;
    createdAt: number;
    ownerUsername: string | null;
}

// Drop anything older than this on load — guards against an orphaned entry
// left behind by a crashed tab/session that never cleared itself.
const MAX_SESSION_AGE_MS = 2 * 60 * 60 * 1000;

export const saveTaskProgressSession = (storageKey: string, session: TaskProgressSession): void => {
    try {
        localStorage.setItem(storageKey, JSON.stringify(session));
    } catch {
        // localStorage unavailable (private mode, quota, etc.) — progress bar
        // simply won't survive a reload, no functional impact otherwise.
    }
};

export const loadTaskProgressSession = (storageKey: string): TaskProgressSession | null => {
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as TaskProgressSession;
        if (!parsed?.sessionId || !parsed?.totalTasks) return null;
        if (Date.now() - (parsed.createdAt || 0) > MAX_SESSION_AGE_MS) return null;
        return parsed;
    } catch {
        return null;
    }
};

export const clearTaskProgressSession = (storageKey: string): void => {
    try {
        localStorage.removeItem(storageKey);
    } catch {
        // no-op
    }
};
